/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react"

import eq from "lodash-es/eq"

import { generateUuid, reduxBatch } from "@/3514/utils"

import { useCoachingChatSocketReceiver as useSocketReceiver } from "../api"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EApiMode,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatMessageAuthor as EMessageAuthor,
    ECoachingChatConversationMessagePhase as EMessagePhase,
    ECoachingChatMessageTransport as EMessageTransport,
    ECoachingChatSocketListenerEventName as ESocketEventName,
    type TCoachingChatSocketBroadcastEventResponse as TBroadcastEventResponse,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    tabId: { get: getTabId }
} = uiUtils

function useBroadcaster({ entityId, entityMode, wasChatCompleted }: _TParams): void {
    const apiMode: EApiMode = useConversationApiMode()

    const conversationId: number | null = useConversationIdSelector(entityId)

    const dispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const isEnabled: boolean = eq(apiMode, EApiMode.Sockets) && eq(entityMode, EEntityMode.Full) && !wasChatCompleted

    const handler: (response: TBroadcastEventResponse) => void = useCallback(
        (response: TBroadcastEventResponse): void => {
            if (!eq(response.conversation_id, conversationId)) return

            reduxBatch((): void => {
                dispatcher.clearError()

                if (eq(response.tab_uuid, getTabId()))
                    dispatcher.replaceTempMessage(response.participant_message_id, response.client_message_uuid)
                else {
                    dispatcher.addMessage({
                        conversationId,
                        transport: EMessageTransport.Sockets,
                        id: response.participant_message_id,
                        content: response.text,
                        clientMessageId: response.client_message_uuid,
                        author: EMessageAuthor.User,
                        isStreamable: false,
                        status: EMessagePhase.Done
                    })
                    dispatcher.addMessage({
                        conversationId,
                        transport: EMessageTransport.Sockets,
                        id: response.participant_message_id + 1,
                        content: String(""),
                        clientMessageId: generateUuid(),
                        author: EMessageAuthor.AI,
                        isStreamable: true,
                        status: EMessagePhase.Waiting
                    })
                    dispatcher.setInputValueRaw(String(""))
                    dispatcher.markInput({ isDimmed: true, isDisabled: true })
                }
            })
        },
        [conversationId]
    )

    useSocketReceiver<TBroadcastEventResponse>({
        isEnabled,
        event: ESocketEventName.Broadcast,
        handler
    })
}

export { useBroadcaster as useCoachingChatBroadcaster }
